import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert, Autocomplete, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  MenuItem, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useToast } from '../../context/ToastContext';
import { usePermission } from '../../hooks/usePermission';
import { PERMISSIONS } from '../../constants/permissions';
import { FixedAssetCountService } from '../../services/fixedAssetCount.service';
import { FixedAssetTransferService } from '../../services/fixedAssetTransfer.service';
import { AssetLocation } from '../../interfaces/fixedAsset/fixedAssetTransfer.interface';
import {
  AssetCondition, AssetCount, AssetCountLine, CONDITION_LABELS, COUNT_RESULTS, COUNT_RESULT_COLORS,
  COUNT_RESULT_LABELS, CountResult,
} from '../../interfaces/fixedAsset/fixedAssetCount.interface';
import ScanDialog from '../../scan/ScanDialog';
import { parseScanUrl } from '../../scan/parseScanUrl';
import { humanizeEnum } from '../../utils/format';

/** One asset count: scan assets, watch the tallies, fix lines, then close (unscanned become not found) and export. */
const FixedAssetCountDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();
  const canVerify = usePermission(PERMISSIONS.FIXED_ASSET_VERIFY);
  const canManage = usePermission(PERMISSIONS.FIXED_ASSET_MANAGE);
  const [count, setCount] = useState<AssetCount | null>(null);
  const [lines, setLines] = useState<AssetCountLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [resultFilter, setResultFilter] = useState<CountResult | ''>('');
  const [q, setQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [scanOpen, setScanOpen] = useState(false);
  const [locations, setLocations] = useState<AssetLocation[]>([]);
  const [scanLocation, setScanLocation] = useState<AssetLocation | null>(null);
  const [lastScan, setLastScan] = useState<{ ok: boolean; text: string } | null>(null);
  const [editing, setEditing] = useState<AssetCountLine | null>(null);
  const [editCondition, setEditCondition] = useState<AssetCondition | ''>('');
  const [editNote, setEditNote] = useState('');
  const [busy, setBusy] = useState(false);

  const open = count?.status === 'OPEN';

  const loadCount = useCallback(async () => {
    if (!id) return;
    try {
      setCount(await FixedAssetCountService.get(id));
    } catch (e) {
      toast.showApiError(e, 'Failed to load the count');
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadLines = useCallback(async () => {
    if (!id) return;
    try {
      const res = await FixedAssetCountService.lines(id, resultFilter, appliedQ);
      setLines(Array.isArray(res) ? res : []);
    } catch (e) {
      toast.showApiError(e, 'Failed to load the count lines');
    }
  }, [id, resultFilter, appliedQ]); // eslint-disable-line react-hooks/exhaustive-deps

  // The search box applies 300 ms after the last keystroke, so typing does not fire a request per letter.
  useEffect(() => {
    const t = setTimeout(() => setAppliedQ(q), 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    setLoading(true);
    loadCount().finally(() => setLoading(false));
  }, [loadCount]);
  useEffect(() => { loadLines(); }, [loadLines]);
  useEffect(() => {
    if (!canVerify) return;
    FixedAssetTransferService.locations().then(r => setLocations(Array.isArray(r) ? r : [])).catch(() => setLocations([]));
  }, [canVerify]);

  const handleScan = async (text: string) => {
    if (!id) return;
    // A label QR carries /q/fa/<id>; a typed or USB-scanned code is the asset code itself.
    const target = parseScanUrl(text);
    if (/^[a-z][a-z0-9+.-]*:\/\//i.test(text.trim()) && !(target && target.type === 'fa')) {
      setLastScan({ ok: false, text: 'That code is not an asset label.' });
      return;
    }
    if (target && target.type !== 'fa') {
      setLastScan({ ok: false, text: 'That code is not an asset label.' });
      return;
    }
    try {
      const line = await FixedAssetCountService.scan(id, {
        ...(target ? { assetId: target.id } : { assetCode: text.trim() }),
        countedLocationId: scanLocation?.id,
      });
      setLastScan({ ok: true, text: `${line.assetCode} - ${line.assetName}: ${COUNT_RESULT_LABELS[line.result]}` });
      loadCount();
      loadLines();
    } catch (e) {
      setLastScan({ ok: false, text: 'Could not record that scan.' });
      toast.showApiError(e, 'Could not record that scan');
    }
  };

  const act = async (fn: () => Promise<unknown>, done: string, fail: string) => {
    setBusy(true);
    try {
      await fn();
      toast.showSuccess(done);
      await Promise.all([loadCount(), loadLines()]);
    } catch (e) {
      toast.showApiError(e, fail);
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    const pending = count?.resultCounts.PENDING ?? 0;
    if (!window.confirm(`Close this count? ${pending} unscanned asset${pending === 1 ? '' : 's'} will be marked not found.`)) return;
    act(() => FixedAssetCountService.close(id!), 'Count closed', 'Failed to close the count');
  };
  const cancel = () => {
    if (!window.confirm('Cancel this count? The results are kept but it can no longer be scanned.')) return;
    act(() => FixedAssetCountService.cancel(id!), 'Count cancelled', 'Failed to cancel the count');
  };

  const startEdit = (l: AssetCountLine) => {
    setEditing(l); setEditCondition(l.condition ?? ''); setEditNote(l.note ?? '');
  };
  const saveEdit = async () => {
    if (!editing || !id) return;
    setBusy(true);
    try {
      await FixedAssetCountService.updateLine(id, editing.id, { condition: editCondition || null, note: editNote });
      setEditing(null);
      await loadLines();
    } catch (e) {
      toast.showApiError(e, 'Failed to save the line');
    } finally {
      setBusy(false);
    }
  };

  const exportFile = (kind: 'csv' | 'pdf') => {
    const p = kind === 'csv' ? FixedAssetCountService.exportCsv(id!, resultFilter, appliedQ) : FixedAssetCountService.exportPdf(id!, resultFilter, appliedQ);
    p.catch(e => toast.showApiError(e, 'Export failed'));
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  if (!count) return <Box sx={{ p: 3 }}><Alert severity="error">Count not found.</Alert></Box>;

  return (
    <Box sx={{ p: 3 }} data-help-page="fixedAssets.countDetail.page">
      <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'center', mb: 2, gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h5">{count.refNo} - {count.name}</Typography>
          <Chip size="small" label={humanizeEnum(count.status)} color={count.status === 'OPEN' ? 'info' : count.status === 'CLOSED' ? 'success' : 'default'} />
        </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
          <Button data-help="fixedAssets.cd.action.all" variant="outlined" onClick={() => navigate('/fixed-assets/counts')}>All counts</Button>
          <Button data-help="fixedAssets.countDetail.exportCsv" variant="outlined" onClick={() => exportFile('csv')}>Export CSV</Button>
          <Button data-help="fixedAssets.countDetail.exportPdf" variant="outlined" onClick={() => exportFile('pdf')}>Export PDF</Button>
          {open && canVerify && (
            <Button data-help="fixedAssets.countDetail.scan" variant="contained" onClick={() => { setLastScan(null); setScanOpen(true); }}>Scan assets</Button>
          )}
          {open && canManage && (
            <>
              <Button data-help="fixedAssets.countDetail.close" variant="contained" color="success" disabled={busy} onClick={close}>Close count</Button>
              <Button data-help="fixedAssets.countDetail.cancel" variant="outlined" color="warning" disabled={busy} onClick={cancel}>Cancel count</Button>
            </>
          )}
        </Box>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Scope: {[...count.locationNames, ...count.departmentNames].join(', ') || 'All assets'}
        {count.startedByName ? ` | Started by ${count.startedByName}` : ''}
        {count.closedByName ? ` | Closed by ${count.closedByName}` : ''}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }} data-testid="count-tallies" data-help-section="fixedAssets.cd.tallies">
        <Chip label={`Total ${count.totalLines}`} />
        {COUNT_RESULTS.map(r => (
          <Chip key={r} color={COUNT_RESULT_COLORS[r]} variant={resultFilter === r ? 'filled' : 'outlined'}
            label={`${COUNT_RESULT_LABELS[r]} ${count.resultCounts[r] ?? 0}`}
            onClick={() => setResultFilter(resultFilter === r ? '' : r)} />
        ))}
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }} data-help-section="fixedAssets.cd.filter">
        <TextField data-help="fixedAssets.cd.filter.result" select label="Result" size="small" value={resultFilter}
          onChange={e => setResultFilter(e.target.value as CountResult | '')} sx={{ flex: '1 1 180px', maxWidth: 240 }}>
          <MenuItem value="">All</MenuItem>
          {COUNT_RESULTS.map(r => <MenuItem key={r} value={r}>{COUNT_RESULT_LABELS[r]}</MenuItem>)}
        </TextField>
        <TextField data-help="fixedAssets.cd.filter.search" label="Search code or name" size="small" value={q} onChange={e => setQ(e.target.value)}
          sx={{ flex: '1 1 220px', maxWidth: 320 }} />
      </Box>

      <TableContainer component={Paper}>
        <Table size="small" data-help-section="fixedAssets.cd.table">
          <TableHead>
            <TableRow>
              <TableCell data-help="fixedAssets.cd.col.code">Code</TableCell>
              <TableCell data-help="fixedAssets.cd.col.name">Name</TableCell>
              <TableCell data-help="fixedAssets.cd.col.expLoc">Expected location</TableCell>
              <TableCell data-help="fixedAssets.cd.col.expCust">Expected custodian</TableCell>
              <TableCell data-help="fixedAssets.cd.col.result">Result</TableCell>
              <TableCell data-help="fixedAssets.cd.col.cntLoc">Counted location</TableCell>
              <TableCell data-help="fixedAssets.cd.col.condition">Condition</TableCell>
              <TableCell data-help="fixedAssets.cd.col.note">Note</TableCell>
              {open && canVerify && <TableCell data-help="fixedAssets.cd.col.actions" />}
            </TableRow>
          </TableHead>
          <TableBody>
            {lines.map(l => (
              <TableRow key={l.id} hover>
                <TableCell>{l.assetCode}</TableCell>
                <TableCell>{l.assetName}</TableCell>
                <TableCell>{l.expectedLocationName ?? '—'}</TableCell>
                <TableCell>{l.expectedCustodianName ?? '—'}</TableCell>
                <TableCell><Chip size="small" color={COUNT_RESULT_COLORS[l.result]} label={COUNT_RESULT_LABELS[l.result]} /></TableCell>
                <TableCell>{l.countedLocationName ?? '—'}</TableCell>
                <TableCell>{l.condition ? CONDITION_LABELS[l.condition] : '—'}</TableCell>
                <TableCell>{l.note ?? '—'}</TableCell>
                {open && canVerify && (
                  <TableCell><Button data-help="fixedAssets.cd.action.edit" size="small" onClick={() => startEdit(l)}>Edit</Button></TableCell>
                )}
              </TableRow>
            ))}
            {lines.length === 0 && (
              <TableRow><TableCell colSpan={open && canVerify ? 9 : 8} align="center">No lines match.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <ScanDialog open={scanOpen} continuous onClose={() => setScanOpen(false)} onScan={handleScan}
        title={`Scan assets for ${count.refNo}`} manualLabel="Asset code (or paste a label link)">
        <Autocomplete<AssetLocation> size="small" options={locations} value={scanLocation} sx={{ mt: 2 }}
          onChange={(_, v) => setScanLocation(v)} getOptionLabel={l => `${l.code} - ${l.name}`}
          isOptionEqualToValue={(a, b) => a.id === b.id}
          renderInput={p => <TextField data-help="fixedAssets.countScan.field.location" {...p} label="Counting in (optional)" />} />
        {lastScan && (
          <Alert severity={lastScan.ok ? 'success' : 'error'} sx={{ mt: 1 }} data-testid="last-scan">{lastScan.text}</Alert>
        )}
      </ScanDialog>

      <Dialog data-help-page="fixedAssets.countLine.page" open={!!editing} onClose={() => setEditing(null)} fullWidth maxWidth="xs">
        <DialogTitle>{editing ? `${editing.assetCode} - ${editing.assetName}` : ''}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField data-help="fixedAssets.countLine.field.condition" select label="Condition" size="small" value={editCondition}
              onChange={e => setEditCondition(e.target.value as AssetCondition | '')}>
              <MenuItem value="">Not recorded</MenuItem>
              {(Object.keys(CONDITION_LABELS) as AssetCondition[]).map(c => (
                <MenuItem key={c} value={c}>{CONDITION_LABELS[c]}</MenuItem>
              ))}
            </TextField>
            <TextField data-help="fixedAssets.countLine.field.note" label="Note" size="small" multiline minRows={2} value={editNote} onChange={e => setEditNote(e.target.value)}
              inputProps={{ maxLength: 500 }} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button data-help="fixedAssets.countLine.cancel" onClick={() => setEditing(null)} disabled={busy}>Cancel</Button>
          <Button data-help="fixedAssets.cd.action.save" variant="contained" onClick={saveEdit} disabled={busy}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FixedAssetCountDetail;
